import React, { useCallback, useEffect } from 'react';
import { useWordDataContext } from '../contexts/WordDataContext';
import { calculatePercentagePosition, type WordData, type BlockData } from '../config/wordOverlayConfig';
import type { PageData, PageType } from '../types/editing';
import BlockOverlay from './BlockOverlay';
import InlinePageTypeEditor from './InlinePageTypeEditor';

interface PageEditorProps {
  pageIndex: number;
  imageDimensions: { width: number; height: number };
  onWordClick?: (word: WordData, block: BlockData) => void;
  isbn: string;
  wordsFilename: string | null;
  showWords: boolean;
  pageData?: PageData;
  currentLeftPage?: number;
  currentRightPage?: number;
  showOverlays?: boolean; // When true, show overlays for spread pages regardless of editor state
  onPageTypeChange?: (pageIndex: number, pageType: PageType) => void;
}

/**
 * PageEditor - Renders word and block overlays for page editing
 */
const PageEditor: React.FC<PageEditorProps> = ({
  pageIndex,
  imageDimensions,
  onWordClick,
  isbn,
  wordsFilename,
  showWords,
  pageData,
  currentLeftPage,
  currentRightPage,
  showOverlays = false,
  onPageTypeChange
}) => {
  const { state: wordDataState, actions } = useWordDataContext();
  
  // Calculate if this page is in the active spread
  const isInSpread = currentLeftPage !== undefined && currentRightPage !== undefined &&
    (pageIndex === currentLeftPage || pageIndex === currentRightPage);
  
  // Get blocks from WordDataContext for this page
  const blocks = wordDataState.blocksByPage.get(pageIndex) || [];
  
  // Determine if we should render overlays - always render if in spread (SpreadEditor manages hide-overlays class)
  const shouldRender = wordsFilename && isInSpread && blocks.length > 0;
  
  // Sync hide-overlays class when showOverlays changes
  useEffect(() => {
    if (!shouldRender) return;
    const overlayContainer = document.querySelector(`.word-overlay-container[data-page-overlay="page-${pageIndex}"]`);
    if (overlayContainer) {
      if (!showOverlays) {
        overlayContainer.classList.add('hide-overlays');
      } else {
        overlayContainer.classList.remove('hide-overlays');
      }
    }
  }, [showOverlays, pageIndex, shouldRender]);

  const handleWordClick = useCallback((word: WordData, block: BlockData) => {
    actions.setSelectedWord(String(word.wordId), pageIndex);
    onWordClick?.(word, block);
  }, [onWordClick, actions, pageIndex]);

  const getPageTypeDisplay = (pageType?: string) => {
    if (!pageType || pageType.trim() === '') return '';
    return pageType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getPageTypeColor = (pageType?: string) => {
    const type = pageType && pageType.trim() !== '' ? pageType : 'unknown';
    const colorMap: { [key: string]: string } = {
      'cover': '#e74c3c',           // Red
      'title': '#3498db',           // Blue
      'content': '#2ecc71',         // Green
      'copyright': '#9b59b6',       // Purple
      'table-of-contents': '#f39c12', // Orange
      'index': '#1abc9c',           // Turquoise
      'multi': '#34495e',           // Dark gray
      'back-page': '#95a5a6',       // Light gray
      'back-cover': '#e67e22',      // Dark orange
      'blank-page': '#bdc3c7',      // Very light gray
      'glossary': '#8e44ad',        // Dark purple
      'bibliography': '#16a085',    // Dark turquoise
      'dedication': '#d35400',      // Dark red
      'epigraph': '#7f8c8d',        // Gray
      'foreword': '#27ae60',        // Dark green
      'preface': '#2980b9',         // Dark blue
      'acknowledgments': '#c0392b', // Dark red
      'appendix': '#8e44ad',        // Dark purple
      'notes': '#f1c40f',           // Yellow
      'about-author': '#e67e22',    // Dark orange
      'advertisement': '#e91e63',   // Pink
      'abstract': '#607d8b',        // Blue gray
      'abbreviations': '#795548',    // Brown
      'chronology': '#009688',      // Teal
      'maps': '#4caf50',            // Light green
      'character-list': '#ff9800',  // Amber
      'genealogy': '#673ab7',       // Deep purple
      'pronunciation': '#ff5722',   // Deep orange
      'other-non-content': '#9e9e9e', // Medium gray
      'unknown': '#cccccc'          // Light gray for empty/unknown types
    };
    return colorMap[type] || '#cccccc'; // Default to light gray for unknown types
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <div 
      className={`word-overlay-container ${!showOverlays ? 'hide-overlays' : ''}`} 
      data-page-overlay={`page-${pageIndex}`}
    >
      {/* Page type editor - clickable dropdown */}
      <div className="page-type-badge" style={{ background: 'transparent', border: 'none', padding: 0, boxShadow: 'none', backdropFilter: 'none', maxWidth: 'none', width: 'auto' }}>
        <InlinePageTypeEditor
          pageType={(pageData?.pageType || '') as PageType}
          pageIndex={pageIndex}
          pageLabel={currentLeftPage === pageIndex ? 'Left' : currentRightPage === pageIndex ? 'Right' : 'Left'}
          onChange={(newPageType) => {
            if (onPageTypeChange) {
              onPageTypeChange(pageIndex, newPageType);
            }
          }}
          getPageTypeColor={getPageTypeColor}
          getPageTypeDisplay={getPageTypeDisplay}
        />
      </div>

      {/* Page info box */}
      <div className="page-info-box">
        {`${isbn}|${pageIndex}|?|?|-`}
      </div>

      {blocks.map((block, blockIndex) => {
        return (
          <BlockOverlay
            key={`${block.blockId}-${pageIndex}`}
            block={block}
            blockIndex={blockIndex}
            pageIndex={pageIndex}
            imageDimensions={imageDimensions}
            highlightedWords={wordDataState.highlightedWords}
            onWordClick={handleWordClick}
          />
        );
      })}

    </div>
  );
};

export default PageEditor;